// src/pages/CreateZone.js
import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const baseUrl = config.baseURL;

const LOCATION_OPTIONS = [
  "sidebar",
  "navbar",
  "main_top",
  "main_bottom",
  "footer",
  "custom",
];

const CreateZone = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    description: "",
    width: "",
    height: "",
    max_ads: 1,
    location: "main_top", // default
    page_name: ["dashboard"],
    order_index: 0,
  });
  const [modules, setModules] = useState([
    "dashboard",
    "customer",
    "categories",
    "complaint",
    "earnings",
    "expenses",
    "faqs",
    "invoice",
    "materials",
    "orders",
    "pages",
    "ProductionBatch",
    "products",
    "rewardPoints",
    "ShippingCompany",
    "suppliers",
    "rewardsettings",
    "pricing",
    "edit profile",
    "change password",
    "logout",
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.location) {
      setError("Name and Location are required.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${baseUrl}/api/zone`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.id) {
        toast.success("Zone created successfully!");
        navigate("/dashboard/Zones");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to create zone.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", mt: 6, px: 2 }}>
      <Typography
        variant="h4"
        mb={4}
        sx={{ fontWeight: "bold", textAlign: "center", color: "#2E4053" }}
      >
        Create New Zone
      </Typography>
      <ToastContainer position="top-right" autoClose={5000} />

      <form onSubmit={handleSubmit}>
        <Paper
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 4,
            boxShadow: 4,
            backgroundColor: "#f9f9f9",
            transition: "0.3s",
            "&:hover": { boxShadow: 6 },
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Zone Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
                sx={{ "& .MuiInputBase-input": { fontSize: 15 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                fullWidth
                multiline
                required
                rows={4}
                sx={{ "& .MuiInputBase-input": { fontSize: 15 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Width in pixel"
                name="width"
                type="number"
                required
                value={form.width}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Height in pixel"
                name="height"
                type="number"
                required
                value={form.height}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Max Ads"
                name="max_ads"
                type="number"
                required
                value={form.max_ads}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required disabled>
                <InputLabel>Location</InputLabel>
                <Select
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  label="Location"
                >
                  {LOCATION_OPTIONS.map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc.replace("_", " ").toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Page Name</InputLabel>
                <Select
                  multiple
                  name="page_name"
                  required
                  value={form.page_name} // must be an array
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, page_name: e.target.value }))
                  }
                  label="Page Name"
                >
                  {modules.map((mod) => (
                    <MenuItem key={mod} value={mod}>
                      {mod.charAt(0).toUpperCase() + mod.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Order Index"
                name="order_index"
                type="number"
                value={form.order_index}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mt: 2,
            mb: 4,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: "#2E4053",
              "&:hover": { backgroundColor: "#c7570f" },
              fontWeight: "bold",
              textTransform: "none",
              px: 4,
              py: 1.5,
              borderRadius: 3,
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Create Zone"}
          </Button>
          <Button
            variant="outlined"
            sx={{
              borderColor: "#2E4053",
              color: "#2E4053",
              "&:hover": { borderColor: "#c7570f", color: "#c7570f" },
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: "bold",
              textTransform: "none",
            }}
            onClick={() => navigate("/dashboard/zones")}
          >
            Cancel
          </Button>
        </Box>

        {error && (
          <Typography
            color="error"
            sx={{ mt: 2, textAlign: "center", fontWeight: "bold" }}
          >
            {error}
          </Typography>
        )}
      </form>
    </Box>
  );
};

export default CreateZone;
